import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Field2 as Field,
  VibeModal,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import './HdmiSettings.scss';

const OVERRIDE_HELP_TEXT = 'Override hardware defaults. This may cause content to stretch, crop, or misalign.';
const RESTORE_WARNING_TEXT = 'Turning off the override resets this display to standard algorithms. '
  + 'If this screen was overridden to fix a legacy controller glitch, those display issues may return.';
const OVERRIDE_ERROR_TEXT = 'Enter a whole number of 0 or more';

// VW-7777: whole, non-negative integers only. The API rejects anything else.
export const HDMI_OVERRIDE_VALUE_REGEX = /^\d+$/;

function toDisplayValue(value) {
  // VW-7777: display 0 as "0". The API returns 0 when no override has
  // ever been set; previously it was hidden as blank, which made the
  // user think the stored value was missing.
  return value ?? '';
}

function isOverrideValueValid(value) {
  return HDMI_OVERRIDE_VALUE_REGEX.test(String(value ?? ''));
}

/**
 * Returns true when the HDMI override is off, or when both override
 * values are whole numbers of 0 or more. Used by the Location Save
 * guard in the sidebar and profile.
 */
export function isHdmiOverrideValid(displaySettings) {
  const settings = displaySettings || {};

  if (!settings.displayInterfaceOverrideEnabled) {
    return true;
  }

  const [width, height] = settings.displayInterfaceOverrideResolution || [];

  return isOverrideValueValid(width) && isOverrideValueValid(height);
}

function HdmiSettings({
  className = '',
  displaySettings: displaySettingsProp,
  canEdit = false,
  disableInput = false,
  onUpdate = () => {},
}) {
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const isDisabled = !canEdit || disableInput;

  const displaySettings = displaySettingsProp || {};
  const displayInterfaceOverrideEnabled = Boolean(
    displaySettings.displayInterfaceOverrideEnabled,
  );
  const displayInterfaceOverrideResolution = displaySettings.displayInterfaceOverrideResolution
    || ['', ''];

  const overrideWidth = toDisplayValue(displayInterfaceOverrideResolution[0]);
  const overrideHeight = toDisplayValue(displayInterfaceOverrideResolution[1]);

  const patchDisplaySettings = (patch) => {
    onUpdate({
      displaySettings: {
        ...displaySettings,
        ...patch,
      },
    });
  };

  const onToggleOverride = (toggled) => {
    if (!toggled) {
      setShowRestoreModal(true);
      return;
    }

    // VW-7777: starting the override fresh with API defaults of [0, 0]
    // (or no stored resolution at all) would show 0s in the fields now
    // that we display 0 as "0". Blank the inputs instead so the user
    // has to explicitly enter the override values.
    const [storedWidth, storedHeight] = displayInterfaceOverrideResolution;
    const bothEmptyOrZero = (storedWidth === 0 || storedWidth === '' || storedWidth == null)
      && (storedHeight === 0 || storedHeight === '' || storedHeight == null);

    patchDisplaySettings({
      displayInterfaceOverrideEnabled: true,
      ...(bothEmptyOrZero
        ? { displayInterfaceOverrideResolution: ['', ''] }
        : {}),
    });
  };

  const onConfirmRestore = () => {
    patchDisplaySettings({
      displayInterfaceOverrideEnabled: false,
    });
    setShowRestoreModal(false);
  };

  const onCloseRestore = () => {
    setShowRestoreModal(false);
  };

  const onChangeOverrideResolution = (index) => (e) => {
    const next = [...displayInterfaceOverrideResolution];
    next[index] = e.target.value;
    patchDisplaySettings({
      displayInterfaceOverrideResolution: next,
    });
  };

  return (
    <div
      className={classNames('HdmiSettings', className)}
    >
      <div className="hdmi-settings-header">
        <div className="hdmi-settings-title">
          HDMI Settings
        </div>
      </div>

      <Grid spacing={2} container>
        <Grid size={{ xs: 12 }}>
          <Field
            type="toggle"
            label="HDMI Interface"
            toggleProps={{
              label: 'Override HDMI Defaults',
              helpText: OVERRIDE_HELP_TEXT,
              helpTextStyle: {
                fontStyle: 'italic',
                fontWeight: 'normal',
              },
            }}
            value={displayInterfaceOverrideEnabled
              ? 'on'
              : 'off'}
            disabled={isDisabled}
            tabIndex={20}
            testId="location-hdmi-override-toggle"
            onChange={onToggleOverride}
          />
        </Grid>

        {displayInterfaceOverrideEnabled && (
          <>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Resolution Width"
                name="displayInterfaceOverrideWidth"
                innerLabelProps={{
                  text: 'px',
                }}
                value={overrideWidth}
                onChange={onChangeOverrideResolution(0)}
                disabled={isDisabled}
                required
                // VW-7777: inline guard against blanks, text and
                // negatives / decimals, matching the API validator.
                validation={{
                  check: true,
                  regex: HDMI_OVERRIDE_VALUE_REGEX,
                  errorText: OVERRIDE_ERROR_TEXT,
                  events: ['onBlur'],
                }}
                tabIndex={21}
                testId="location-hdmi-override-width-input"
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Field
                type="text"
                label="Resolution Height"
                name="displayInterfaceOverrideHeight"
                innerLabelProps={{
                  text: 'px',
                }}
                value={overrideHeight}
                onChange={onChangeOverrideResolution(1)}
                disabled={isDisabled}
                required
                validation={{
                  check: true,
                  regex: HDMI_OVERRIDE_VALUE_REGEX,
                  errorText: OVERRIDE_ERROR_TEXT,
                  events: ['onBlur'],
                }}
                tabIndex={22}
                testId="location-hdmi-override-height-input"
              />
            </Grid>
          </>
        )}
      </Grid>

      <VibeModal
        show={showRestoreModal}
        type="confirm"
        title="Restore Display Settings"
        confirmProps={{
          text: 'Ok',
        }}
        testIds={{
          root: 'location-hdmi-restore-dialog',
          confirm: 'location-hdmi-restore-confirm-button',
          cancel: 'location-hdmi-restore-cancel-button',
        }}
        onConfirm={onConfirmRestore}
        onClose={onCloseRestore}
      >
        {RESTORE_WARNING_TEXT}
      </VibeModal>
    </div>
  );
}

const numericPair = PropTypes.arrayOf(PropTypes.oneOfType([
  PropTypes.number,
  PropTypes.string,
]));

HdmiSettings.propTypes = {
  className: PropTypes.string,
  displaySettings: PropTypes.shape({
    displayInterfaceOverrideEnabled: PropTypes.bool,
    displayInterfaceOverrideResolution: numericPair,
  }),
  canEdit: PropTypes.bool,
  disableInput: PropTypes.bool,
  onUpdate: PropTypes.func,
};

export default HdmiSettings;
